import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Code, Eye, Shield, Check, X, ArrowRight } from '../components/serac/icons-lib';
import Button from '../components/common/Button';
import MarketingHeader from '../components/common/MarketingHeader';
import MarketingFooter from '../components/common/MarketingFooter';

const roleDefs = [
  { key: 'developer', icon: Code, color: 'blue' },
  { key: 'stakeholder', icon: Eye, color: 'purple' },
  { key: 'licenseHolder', icon: Shield, color: 'emerald' },
] as const;

const colorMap = {
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200',
    badge: 'bg-blue-50 text-blue-700',
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
    border: 'border-purple-200',
    badge: 'bg-purple-50 text-purple-700',
  },
  emerald: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    badge: 'bg-emerald-50 text-emerald-700',
  },
} as const;

type MatrixKey = 'tuiAccess' | 'portalChat' | 'snRead' | 'snWrite' | 'mcpTools' | 'activity' | 'stories' | 'aiConfig' | 'userMgmt' | 'billing' | 'credentials' | 'analytics' | 'notifications' | 'updateSets';
type ValueKey = 'fullAccess' | 'viaPortal' | 'allTools' | 'ownActivities' | 'allTeamActivities' | 'viaInstanceChat' | 'ownCredentials' | 'orgCredentials' | 'ownUsage' | 'teamOverview' | 'fullAnalytics' | 'own' | 'orgWide' | 'viewOnly';
type Cell = boolean | ValueKey;

const matrixDefs: { key: MatrixKey; developer: Cell; stakeholder: Cell; licenseHolder: Cell }[] = [
  { key: 'tuiAccess', developer: 'fullAccess', stakeholder: false, licenseHolder: false },
  { key: 'portalChat', developer: true, stakeholder: true, licenseHolder: true },
  { key: 'snRead', developer: true, stakeholder: 'viaPortal', licenseHolder: false },
  { key: 'snWrite', developer: true, stakeholder: false, licenseHolder: false },
  { key: 'mcpTools', developer: 'allTools', stakeholder: false, licenseHolder: false },
  { key: 'activity', developer: 'ownActivities', stakeholder: 'allTeamActivities', licenseHolder: 'allTeamActivities' },
  { key: 'stories', developer: true, stakeholder: 'viaInstanceChat', licenseHolder: false },
  { key: 'aiConfig', developer: true, stakeholder: false, licenseHolder: true },
  { key: 'userMgmt', developer: false, stakeholder: false, licenseHolder: true },
  { key: 'billing', developer: false, stakeholder: false, licenseHolder: true },
  // No `orgSettings` row here on purpose: the label it rendered promised SSO,
  // and SSO does not exist — sso_config is empty and /api/sso/start is not
  // mounted. Its label key `roles.matrix.orgSettings` was removed from both
  // locales/en/auth.json and locales/nl/auth.json in the same change, so
  // restoring the row means adding the key back too — and only once it names
  // an organization setting the portal actually ships.
  { key: 'credentials', developer: 'ownCredentials', stakeholder: false, licenseHolder: 'orgCredentials' },
  { key: 'analytics', developer: 'ownUsage', stakeholder: 'teamOverview', licenseHolder: 'fullAnalytics' },
  { key: 'notifications', developer: 'own', stakeholder: 'own', licenseHolder: 'orgWide' },
  { key: 'updateSets', developer: 'fullAccess', stakeholder: 'viewOnly', licenseHolder: false },
];

function MatrixCell({ value, t }: { value: Cell; t: (key: string) => string }) {
  if (value === true) {
    return <Check className="w-5 h-5 text-green-500 mx-auto" />;
  }
  if (value === false) {
    return <X className="w-4 h-4 text-gray-300 mx-auto" />;
  }
  return <span className="text-sm text-gray-700">{t(`auth:roles.matrix.values.${value}`)}</span>;
}

export default function Roles() {
  const { t } = useTranslation(['auth', 'common']);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <MarketingHeader rightContent={
        <>
          <Link to="/roles" className="text-sm text-blue-600 font-medium">{t('auth:roles.navRoles')}</Link>
          <Link to="/pricing" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">{t('auth:roles.navPricing')}</Link>
          <Link to="/"><Button variant="primary" size="sm">{t('auth:roles.signIn')}</Button></Link>
        </>
      } />

      <main className="flex-1 flex flex-col">
      {/* Hero */}
      <div className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('auth:roles.heroTitle')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('auth:roles.heroSubtitle')}
          </p>
        </div>
      </div>

      {/* Role cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 flex-1">
        <div className="grid md:grid-cols-3 gap-6">
          {roleDefs.map((role) => {
            const colors = colorMap[role.color];
            const Icon = role.icon;
            const features = t(`auth:roles.${role.key}.features`, { returnObjects: true }) as Record<string, string>;
            return (
              <div
                key={role.key}
                className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6"
              >
                <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colors.badge} mb-3`}>
                  {t(`auth:roles.${role.key}.name`)}
                </div>
                <p className="text-gray-600 text-sm mb-5">{t(`auth:roles.${role.key}.description`)}</p>
                <ul className="space-y-3">
                  {Object.values(features).map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {/* Stakeholder is a real role in the product, but not a seat you can buy
            on a self-serve plan: resolveSeatUsage (routes/team.ts) gives every
            non-contract tier stakeholderCap 0, and the invite modal drops the
            option entirely. Saying so here keeps this page from selling a seat
            that /portal/team refuses to sell. */}
        <p className="mt-8 text-sm text-gray-500 text-center max-w-3xl mx-auto">
          {t('auth:roles.seatNote')}
        </p>
      </div>

      {/* Feature comparison matrix */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('auth:roles.matrixTitle')}
          </h2>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-sm font-medium text-gray-500 px-6 py-4 sticky left-0 bg-white/80 backdrop-blur-sm min-w-[200px]">
                      {t('auth:roles.matrixHeaders.feature')}
                    </th>
                    <th className="text-center text-sm font-medium px-6 py-4 min-w-[140px]">
                      <div className="flex items-center justify-center gap-1.5">
                        <Code className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-600">{t('auth:roles.matrixHeaders.developer')}</span>
                      </div>
                    </th>
                    <th className="text-center text-sm font-medium px-6 py-4 min-w-[140px]">
                      <div className="flex items-center justify-center gap-1.5">
                        <Eye className="w-4 h-4 text-purple-600" />
                        <span className="text-purple-600">{t('auth:roles.matrixHeaders.stakeholder')}</span>
                      </div>
                    </th>
                    <th className="text-center text-sm font-medium px-6 py-4 min-w-[140px]">
                      <div className="flex items-center justify-center gap-1.5">
                        <Shield className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-600">{t('auth:roles.matrixHeaders.licenseHolder')}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matrixDefs.map((feature, index) => (
                    <tr
                      key={feature.key}
                      className={index < matrixDefs.length - 1 ? 'border-b border-gray-100' : ''}
                    >
                      <td className="text-sm text-gray-700 font-medium px-6 py-3.5 sticky left-0 bg-white/80 backdrop-blur-sm">
                        {t(`auth:roles.matrix.${feature.key}`)}
                      </td>
                      <td className="text-center px-6 py-3.5">
                        <MatrixCell value={feature.developer} t={t} />
                      </td>
                      <td className="text-center px-6 py-3.5">
                        <MatrixCell value={feature.stakeholder} t={t} />
                      </td>
                      <td className="text-center px-6 py-3.5">
                        <MatrixCell value={feature.licenseHolder} t={t} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* How it works together */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('auth:roles.stepsTitle')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200 p-6 text-center">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">{t('auth:roles.steps.stepLabel', { n: 1 })}</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{t('auth:roles.steps.stakeholderTitle')}</h3>
              <p className="text-sm text-gray-600">
                {t('auth:roles.steps.stakeholderBody')}
              </p>
            </div>
            <div className="relative bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200 p-6 text-center">
              <ArrowRight className="hidden md:block absolute -left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Code className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">{t('auth:roles.steps.stepLabel', { n: 2 })}</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{t('auth:roles.steps.developerTitle')}</h3>
              <p className="text-sm text-gray-600">
                {t('auth:roles.steps.developerBody')}
              </p>
            </div>
            <div className="relative bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200 p-6 text-center">
              <ArrowRight className="hidden md:block absolute -left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">{t('auth:roles.steps.stepLabel', { n: 3 })}</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{t('auth:roles.steps.licenseHolderTitle')}</h3>
              <p className="text-sm text-gray-600">
                {t('auth:roles.steps.licenseHolderBody')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 pb-16">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{t('auth:roles.ctaTitle')}</h2>
          <p className="text-blue-100 mb-6">
            {t('auth:roles.ctaBody')}
          </p>
          <Link to="/?mode=register">
            <Button
              variant="secondary"
              className="!bg-white/10 text-white hover:!bg-white/20 border border-white/20"
            >
              {t('auth:roles.ctaButton')}
            </Button>
          </Link>
        </div>
      </div>

      </main>

      <MarketingFooter />
    </div>
  );
}
